import React, { useEffect, useState } from 'react';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { ReptileTile } from '../../utilities/ReptileTile.tsx';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { CollectionReptileModal } from './CollectionReptileModal.tsx';
import { CollectionReptile, CollectionReptileGroup } from '../types/CollectionTypes.ts';
import { Morph } from '../../../types/Morph.ts';
import { Gene } from '../../../types/Gene.ts';
import { useRepToolContext } from '../../../RepToolContext.tsx';
import { driver } from 'driver.js';
import { UserInfo } from '../../../types/UserInfo.ts';

const ModalButtons = ({
  deleteActive,
  actionClicked,
  actionReady
}: {
  deleteActive: boolean,
  actionClicked: () => void,
  actionReady: boolean
}) => (
  <div>
    {deleteActive && <div id="delete-btn-list" onClick={actionClicked} className={`my-auto btn btn-outline-${actionReady ? 'danger' : 'secondary'}`}>
      Delete
    </div>}
    {!deleteActive && <div id="assign" onClick={actionClicked} className={`my-auto btn btn-outline-${actionReady ? 'light' : 'secondary'}`}>
      Assign
    </div>}
  </div>
)
const ActionButton = ({
  selectedAction,
  setSelectedAction,
  currentStatus
}: {
  selectedAction: string,
  setSelectedAction: (action: string) => void,
  currentStatus: string
}) => {
  return (
    <div id='action-list'>
      <select onChange={(event) => setSelectedAction(event.target.value)} value={selectedAction} className="">
        <option>Actions</option>
        {currentStatus !== 'available' && <option value="available">Mark as Available</option>}
        {currentStatus !== 'nfs' && <option value="nfs">Mark as Not For Sale</option>}
        {currentStatus !== 'sold' && <option value="sold">Mark as Sold</option>}
        <option value="delete">Delete Selected</option>
      </select>
    </div>
  )
}

export const GroupModal = ({
  closeModal,
  group,
  status,
  filterValue,
  updateGroup,
  morphs,
  setSoldReptiles,
  genes,
  isSpecies,
  userInfo
}: {
  status: string,
  filterValue: string,
  closeModal: () => void,
  group: CollectionReptileGroup,
  updateGroup: (group: CollectionReptileGroup) => void,
  morphs: Morph[],
  setSoldReptiles: (reptiles: CollectionReptile[]) => void,
  genes: Gene[],
  isSpecies: boolean,
  userInfo: UserInfo
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [editingReptile, setEditingReptile] = useState<CollectionReptile | null>(null);
  const [currentReptiles, setCurrentReptiles] = useState<CollectionReptile[]>(group.savedReptiles || []);
  const [selectedAction, setSelectedAction] = useState("");
  const {appendControls} = useRepToolContext();
  let title = filterValue;

  const driverObj = driver({
    steps: [
      {
        element: '#list-grid-container',
        popover: {
          title: 'Reptiles',
          description: 'These are your reptiles, click on a reptile to view its details',
        }
      },
      {
        element: '#action-list',
        popover: {
          title: 'Actions',
          description: 'Select an action to perform on the selected reptiles. To select a reptile, tap anywhere other than its button.',
        }
      },
      {
        element: '#assign',
        popover: {
          title: 'Commit Action',
          description: 'Tap this button when you want to commit the action to the selected reptiles.',
        }
      }
    ]
  });


  const openDetails = (reptile) => {
    appendControls(null, "Open Reptile Details");
    setShowDetails(true);
    setEditingReptile(reptile);
  }
  const toggleSelect = (index) => {
    const newReptiles = [...currentReptiles];
    newReptiles[index].selected = !newReptiles[index].selected;
    setCurrentReptiles(newReptiles);
    setControls();
  }

  const deleteAction = () => {
    // TODO: delete saved reptiles and update group numbers
    const selectedReptiles = currentReptiles.filter((reptile) => reptile.selected);
    const nonSelectedReptiles = currentReptiles.filter((reptile) => !reptile.selected);
    const genders = { "male": 0, "female": 0, "none": 0 };
    selectedReptiles.forEach((reptile) => {
      genders[reptile.gender]++;
    });
    const groupData = {
      reptile: localStorage.getItem('selectedReptile'),
      status: status,
      morphName: group.morph_name,
      males: genders["male"],
      females: genders["female"],
      unknown: genders["none"]
    }

    selectedReptiles.forEach((reptile) => {
      BasicFetch({
        url: `${(window as any).serverLocation}reptile/collection_reptile/delete`,
        data: reptile,
        method: 'DELETE',
        onSuccess: (data) => {
          setCurrentReptiles(nonSelectedReptiles);
          updateGroup({ ...group, reptiles: nonSelectedReptiles });
        },
        onFail: () => {
        },
      })
    })
  }
  const moveAction = () => {
    const selectedReptiles = currentReptiles.filter((reptile) => reptile.selected);
    const nonSelectedReptiles = currentReptiles.filter((reptile) => !reptile.selected);
    const url = `${(window as any).serverLocation}collection_reptile/move/current/${status}/destination/${selectedAction}`;
    BasicFetch({
      url,
      data: {
        reptiles: selectedReptiles,
        reptile: localStorage.getItem('selectedReptile'),
      },
      method: 'POST',
      onSuccess: (data) => {
        setCurrentReptiles(nonSelectedReptiles);
        updateGroup({ ...group, reptiles: nonSelectedReptiles });
        setControls();
      },
      onFail: () => {
      },
    })
  }
  const actionClicked = () => {
    if (selectedAction === "delete") {
      deleteAction()
    } else if (selectedAction === "sold") {
      setSoldReptiles(currentReptiles.filter((reptile) => reptile.selected))
    } else {
      moveAction();
    }
  }


  const setControls = () => {
    appendControls(<React.Fragment>
      <ActionButton selectedAction={selectedAction} currentStatus={status} setSelectedAction={setSelectedAction} />
      <ModalButtons
        deleteActive={selectedAction === "delete"}
        actionClicked={() => actionClicked()}
        actionReady={currentReptiles.find((reptile) => reptile.selected) !== undefined && selectedAction !== ""}
      />
    </React.Fragment>, "Group Modal Set");
  }

  useEffect(() => {
    setControls();
  }, [selectedAction])

  return (
    <React.Fragment>
      <ReptoolModal title={title} closeModal={closeModal} showTutorial={driverObj.drive} body={(
        <React.Fragment>
          <div id="list-grid-container" className="row">
            {currentReptiles.map((reptile, index) => {
              const morph_name = reptile.morph_name?.split(" het")[0] || "Wild Type";
              return (
                <ReptileTile
                  key={index}
                  reptile={reptile}
                  morphInfo={morphs.find(morph => morph.name === morph_name) || morphs.find(morph => morph.name === "Wild Type") || null} onSelected={() => toggleSelect(index)}
                  deleteSelectedActive={selectedAction === "delete"}
                  clickEvent={() => openDetails(reptile)}
                  variant="lg"
                />)
            })}
          </div>
        </React.Fragment>
      )} />
      {showDetails && editingReptile && <CollectionReptileModal
        refreshReptile={(reptile) => {
          const newReptiles = [...currentReptiles];
          const index = newReptiles.findIndex((rep) => rep.id === reptile.id);
          newReptiles[index] = reptile;
          setCurrentReptiles(newReptiles);
        }}
        closeModal={() => { setControls(); setShowDetails(false); }}
        reptileId={editingReptile.uuid}
        readOnly={false}
        morphs={morphs}
        genes={genes}
        isSpecies={isSpecies}
        userInfo={userInfo}
      />}
    </React.Fragment>
  )
}